/**
 * GedcomUploadModal Component
 *
 * Modal for uploading GEDCOM files with encoding detection and parsing.
 * Part of Phase 4 - Slice 1 & 2: GEDCOM Upload + Parser Integration
 *
 * Features:
 * - File picker (.ged, .gedcom only)
 * - File size validation (max 10MB)
 * - Encoding detection and display
 * - Upload progress states
 * - Automatic parsing after upload
 * - Preview table for parsed persons
 * - Bilingual support (en/ar)
 * - RTL-safe design
 */

import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { useGedcomUpload, type UploadedFileMetadata } from '../hooks/useGedcomUpload';
import { useGedcomParse } from '../hooks/useGedcomParse';
import { useGedcomImportPlan } from '../hooks/useGedcomImportPlan';
import { useGedcomImport } from '../hooks/useGedcomImport';
import { getEncodingDisplayName, getConfidenceDisplayName } from '../utils/encodingDetection';
import { GedcomPreviewTable } from './GedcomPreviewTable';
import { GedcomFamilyPreview } from './GedcomFamilyPreview';
import { GedcomValidationSummary } from './GedcomValidationSummary';
import { GedcomImportPlanSummary } from './GedcomImportPlanSummary';

interface GedcomUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (metadata: UploadedFileMetadata) => void;
}

type ViewMode = 'upload' | 'preview';

export function GedcomUploadModal({ isOpen, onClose, onSuccess }: GedcomUploadModalProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('upload');

  const { uploadState, uploadFile, error: uploadError, metadata, reset: resetUpload } = useGedcomUpload();
  const { parseState, parseFile, error: parseError, result: parseResult, reset: resetParse } = useGedcomParse();
  const { state: planState, plan, buildPlan, reset: resetPlan } = useGedcomImportPlan();
  const {
    phase: importPhase,
    executeImport,
    importResult,
    error: importError,
    reset: resetImport
  } = useGedcomImport({
    treeName: selectedFile?.name.replace(/\.(ged|gedcom)$/i, '') || 'Imported Family Tree', // Use filename as default tree name
    onComplete: (result) => {
      if (result.success && result.tree_id) {
        toast.success(t('gedcom.importSuccess', 'Family tree imported successfully!'));
        handleClose();

        // If we're already on /dashboard, reload the page to show the new tree
        // Otherwise, navigate to /dashboard
        if (location.pathname === '/dashboard') {
          window.location.reload();
        } else {
          navigate('/dashboard');
        }
      }
    },
    onError: (error) => {
      toast.error(t('gedcom.importFailed', 'Import failed: {{error}}', { error }));
    }
  });

  /**
   * Auto-parse file after successful upload
   */
  useEffect(() => {
    if (uploadState === 'success' && metadata) {
      handleParse(metadata);
    }
  }, [uploadState, metadata]);

  /**
   * Auto-build import plan after successful parse
   */
  useEffect(() => {
    if (parseState === 'success' && parseResult && planState === 'idle') {
      buildPlan(parseResult);
    }
  }, [parseState, parseResult, planState, buildPlan]);

  /**
   * Handle file selection
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      resetUpload();
      resetParse();
      setViewMode('upload');
    }
  };

  /**
   * Trigger file picker
   */
  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handle file upload
   */
  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      await uploadFile(selectedFile);
      // Parsing will be triggered automatically via useEffect
    } catch (err) {
      // Error is already set in hook state
      console.error('[GedcomUploadModal] Upload failed:', err);
    }
  };

  /**
   * Handle file parsing
   */
  const handleParse = async (fileMetadata: UploadedFileMetadata) => {
    try {
      await parseFile(fileMetadata);
      // Switch to preview mode on success
      setViewMode('preview');
    } catch (err) {
      // Error is already set in hook state
      console.error('[GedcomUploadModal] Parse failed:', err);
    }
  };

  /**
   * Handle preview continue button - trigger actual import
   */
  const handleContinue = async () => {
    // Check if we have everything needed for import
    if (!parseResult || !plan || !selectedFile) {
      toast.error(t('gedcom.missingData', 'Missing required data for import'));
      return;
    }

    // Check if import is blocked
    if (!plan.summary.ready_for_import) {
      toast.error(t('gedcom.importBlocked', 'Import is blocked due to validation errors'));
      return;
    }

    // Execute the import
    try {
      await executeImport(selectedFile, parseResult, plan);
      // Success handling is done in the onComplete callback
    } catch (error) {
      console.error('[GedcomUploadModal] Import failed:', error);
      // Error handling is done in the onError callback
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    const isBusy = uploadState === 'detecting' || uploadState === 'uploading' ||
                   parseState === 'fetching' || parseState === 'parsing' ||
                   importPhase === 'importing';
    if (isBusy) {
      // Don't allow closing during upload/parse/import
      return;
    }
    setSelectedFile(null);
    setViewMode('upload');
    resetUpload();
    resetParse();
    resetPlan();
    resetImport();
    onClose();
  };

  /**
   * Format file size for display
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isUploading = uploadState === 'detecting' || uploadState === 'uploading';
  const isParsing = parseState === 'fetching' || parseState === 'parsing';
  const isSuccess = uploadState === 'success';
  const hasUploadError = uploadState === 'error';
  const hasParseError = parseState === 'error';
  const error = uploadError || parseError;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 animate-in fade-in" />

        {/* Modal Content */}
        <Dialog.Content className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-[95vw] ${
          viewMode === 'preview' ? 'sm:max-w-[700px] md:max-w-[900px]' : 'sm:max-w-[500px] md:max-w-[600px]'
        } bg-white dark:bg-slate-800 rounded-lg sm:rounded-xl shadow-xl animate-in fade-in zoom-in-95 max-h-[90vh] ${
          viewMode === 'preview' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'
        }`}>
          {/* Render Preview or Upload view */}
          {viewMode === 'preview' && parseResult ? (
            <>
              {/* Header */}
              <div className="p-4 sm:p-5 md:p-6 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                  {t('gedcom.preview', 'Preview')}
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t('gedcom.personsFound', {
                    count: parseResult.persons.length,
                    defaultValue: 'Found {{count}} individuals',
                  })}
                  {', '}
                  {t('gedcom.familiesFound', {
                    count: parseResult.resolvedFamilies.length,
                    defaultValue: 'Found {{count}} families',
                  })}
                </p>
              </div>

              {/* Preview Content */}
              <div className="flex-1 overflow-auto p-4 sm:p-5 md:p-6 space-y-6">
                {/* Import Error Display */}
                {importError && (
                  <div className="p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="material-symbols-outlined text-red-600 dark:text-red-400 flex-shrink-0">
                        error
                      </span>
                      <div className="flex-1">
                        <p className="text-sm sm:text-base font-medium text-red-900 dark:text-red-100">
                          {t('gedcom.importError', 'Import failed')}
                        </p>
                        <p className="text-xs sm:text-sm text-red-700 dark:text-red-300 mt-1">{importError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Validation Summary - Show first */}
                <GedcomValidationSummary validation={parseResult.validation} />

                {/* Import Plan Summary - Shows what will be imported */}
                <GedcomImportPlanSummary parseResult={parseResult} />

                {/* Individuals Section */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white mb-3">
                    {t('gedcom.individuals', 'Individuals')}
                  </h4>
                  <GedcomPreviewTable persons={parseResult.persons} />
                </div>

                {/* Families Section */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white mb-3">
                    {t('gedcom.families', 'Families')}
                  </h4>
                  <GedcomFamilyPreview families={parseResult.resolvedFamilies} />
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 sm:p-5 md:p-6 border-t border-slate-200 dark:border-slate-700 flex gap-2 sm:gap-3 justify-end">
                <button
                  onClick={handleClose}
                  className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md sm:rounded-lg transition-colors min-h-[44px]"
                >
                  {t('gedcom.cancel', 'Cancel')}
                </button>

                <button
                  onClick={handleContinue}
                  disabled={
                    parseResult.validation.status === 'blocked' ||
                    importPhase === 'importing' ||
                    planState !== 'ready'
                  }
                  className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-medium text-white bg-primary hover:bg-primary/90 rounded-md sm:rounded-lg transition-colors min-h-[44px] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importPhase === 'importing' ? (
                    <>
                      <div className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t('gedcom.importing', 'Importing...')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('gedcom.import', 'Import')}</span>
                      <span className="material-symbols-outlined text-lg">arrow_forward</span>
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Header */}
              <div className="p-4 sm:p-5 md:p-6 border-b border-slate-200 dark:border-slate-700">
                <Dialog.Title className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
                  {t('gedcom.uploadTitle', 'Import GEDCOM File')}
                </Dialog.Title>
                <Dialog.Description className="text-xs sm:text-sm md:text-base text-slate-500 dark:text-slate-400 mt-1 sm:mt-1.5 md:mt-2">
                  {t('gedcom.uploadDescription', 'Upload a GEDCOM (.ged) file to import your family tree')}
                </Dialog.Description>
              </div>

              {/* Body */}
              <div className="p-4 sm:p-5 md:p-6 space-y-4 sm:space-y-5">
            {/* File Input (Hidden) */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".ged,.gedcom"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* File Selection Button */}
            {!selectedFile && (
              <div className="space-y-3">
                <button
                  onClick={handleSelectFileClick}
                  disabled={isUploading}
                  className="w-full p-6 sm:p-8 border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary dark:hover:border-primary bg-slate-50 dark:bg-slate-900 hover:bg-primary/5 rounded-lg transition-colors flex flex-col items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-4xl sm:text-5xl text-slate-400 dark:text-slate-500">
                    upload_file
                  </span>
                  <div className="text-center">
                    <p className="text-sm sm:text-base font-medium text-slate-700 dark:text-slate-300">
                      {t('gedcom.selectFile', 'Select GEDCOM File')}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {t('gedcom.supportedFormats', 'Supported formats: .ged, .gedcom')}
                    </p>
                  </div>
                </button>

                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 text-center">
                  {t('gedcom.maxFileSize', 'Maximum file size: 10 MB')}
                </p>
              </div>
            )}

            {/* Selected File Info */}
            {selectedFile && (
              <div className="space-y-4">
                {/* File Details Card */}
                <div className="p-4 sm:p-5 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-2xl sm:text-3xl text-primary flex-shrink-0">
                      description
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white mb-1">
                        {t('gedcom.fileSelected', 'File Selected')}
                      </h4>

                      {/* File Name */}
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                            {t('gedcom.fileName', 'File Name')}:
                          </p>
                          <p className="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 break-all">
                            {selectedFile.name}
                          </p>
                        </div>

                        {/* File Size */}
                        <div>
                          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                            {t('gedcom.fileSize', 'File Size')}:
                          </p>
                          <p className="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300">
                            {formatFileSize(selectedFile.size)}
                          </p>
                        </div>

                        {/* Encoding (after detection) */}
                        {metadata && (
                          <div>
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                              {t('gedcom.detectedEncoding', 'Detected Encoding')}:
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300">
                                {getEncodingDisplayName(metadata.detectedEncoding.encoding)}
                              </p>
                              <span
                                className={`inline-block px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded ${
                                  metadata.detectedEncoding.confidence === 'high'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                                    : metadata.detectedEncoding.confidence === 'medium'
                                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                                }`}
                              >
                                {getConfidenceDisplayName(metadata.detectedEncoding.confidence)} Confidence
                              </span>
                            </div>
                            {metadata.detectedEncoding.warning && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                {metadata.detectedEncoding.warning}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Change File Button */}
                    {!isUploading && !isSuccess && (
                      <button
                        onClick={handleSelectFileClick}
                        className="text-xs sm:text-sm text-primary hover:text-primary/80 font-medium flex-shrink-0"
                      >
                        Change
                      </button>
                    )}
                  </div>
                </div>

                {/* Upload States */}
                {uploadState === 'detecting' && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100">
                      {t('gedcom.detecting', 'Detecting encoding...')}
                    </p>
                  </div>
                )}

                {uploadState === 'uploading' && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100">
                      {t('gedcom.uploading', 'Uploading...')}
                    </p>
                  </div>
                )}

                {parseState === 'fetching' && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100">
                      {t('gedcom.fetchingFile', 'Fetching file...')}
                    </p>
                  </div>
                )}

                {parseState === 'parsing' && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100">
                      {t('gedcom.parsingFile', 'Parsing GEDCOM file...')}
                    </p>
                  </div>
                )}

                {isSuccess && !isParsing && parseState !== 'success' && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <span className="material-symbols-outlined text-green-600 dark:text-green-400 flex-shrink-0">
                      check_circle
                    </span>
                    <p className="text-xs sm:text-sm text-green-900 dark:text-green-100">
                      {t('gedcom.uploadSuccess', 'File uploaded successfully')}
                    </p>
                  </div>
                )}

                {hasUploadError && uploadError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <span className="material-symbols-outlined text-red-600 dark:text-red-400 flex-shrink-0">
                      error
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-red-900 dark:text-red-100">
                        {t('gedcom.uploadError', 'Failed to upload file. Please try again.')}
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-300 mt-1">{uploadError.message}</p>
                    </div>
                  </div>
                )}

                {hasParseError && parseError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <span className="material-symbols-outlined text-red-600 dark:text-red-400 flex-shrink-0">
                      error
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-red-900 dark:text-red-100">
                        {t('gedcom.parseError', 'Error parsing GEDCOM file')}
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-300 mt-1">{parseError.message}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

              {/* Footer */}
              <div className="p-4 sm:p-5 md:p-6 border-t border-slate-200 dark:border-slate-700 flex gap-2 sm:gap-3 justify-end">
                <button
                  onClick={handleClose}
                  disabled={isUploading || isParsing}
                  className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md sm:rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {t('gedcom.cancel', 'Cancel')}
                </button>

                {!isSuccess && selectedFile && (
                  <button
                    onClick={handleUpload}
                    disabled={isUploading || isParsing || hasUploadError || hasParseError}
                    className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-medium text-white bg-primary hover:bg-primary/90 rounded-md sm:rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center gap-2"
                  >
                    {isUploading || isParsing ? (
                      <>
                        <div className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>
                          {isUploading
                            ? t('gedcom.uploading', 'Uploading...')
                            : t('gedcom.parsingFile', 'Parsing GEDCOM file...')}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>{t('gedcom.continue', 'Continue')}</span>
                        <span className="material-symbols-outlined text-lg">arrow_forward</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
